import { Component } from '@theme/component';

/**
 * Submits the menu drawer's email signup without navigating.
 *
 * A plain `{% form 'customer' %}` post reloads the page, which closes the drawer and
 * takes the confirmation with it. Posting in place keeps the drawer open so the reply
 * lands where the shopper is looking.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [email] - The email input.
 * @property {HTMLElement} [message] - The status message; `role="status"` announces what we write into it.
 *
 * @extends {Component<Refs>}
 */
class DrawerNewsletter extends Component {
  /** @type {boolean} */
  #pending = false;

  connectedCallback() {
    super.connectedCallback();
    // Bound to the component rather than the form: `submit` bubbles, so this catches the
    // post no matter when the form itself was parsed or re-rendered underneath us.
    this.addEventListener('submit', this.#submit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#submit);
  }

  /**
   * Posts the form and reports the outcome in place.
   *
   * Nothing below this point may navigate: a native submit would reload the page and
   * close the drawer, which is the whole reason this component exists.
   *
   * @param {SubmitEvent} event
   */
  #submit = async (event) => {
    event.preventDefault();

    const form = event.target;

    if (!(form instanceof HTMLFormElement) || this.#pending || !form.reportValidity()) return;

    this.#pending = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        // Shopify redirects to `?customer_posted=true` on success and re-renders this page
        // with errors on failure, so the redirect itself is the answer. Following it would
        // mean re-fetching a whole page and reading a status code that says nothing useful.
        redirect: 'manual',
      });

      if (response.type === 'opaqueredirect' || response.redirected) {
        this.#succeed(form);
        return;
      }

      // A real body means Shopify re-rendered the page; this form's own status node then
      // carries the reason, already translated and worded the way the server would.
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const message = html.getElementById(`${form.id}-message`);

      if (message?.dataset.status === 'error') {
        this.#render(message.textContent?.trim() ?? '', 'error');
      } else {
        this.#succeed(form);
      }
    } catch {
      this.#render(this.dataset.errorMessage ?? '', 'error');
    } finally {
      this.#pending = false;
      this.removeAttribute('aria-busy');
    }
  };

  /**
   * @param {HTMLFormElement} form
   */
  #succeed(form) {
    this.#render(this.dataset.successMessage ?? '', 'success');
    form.reset();
  }

  /**
   * @param {string} text - The message to show, or an empty string to hide it.
   * @param {'success' | 'error'} status
   */
  #render(text, status) {
    const { message } = this.refs;

    if (!message) return;

    message.textContent = text;
    message.dataset.status = status;
    message.hidden = text === '';
  }
}

if (!customElements.get('drawer-newsletter')) {
  customElements.define('drawer-newsletter', DrawerNewsletter);
}
